import type { GenericError, RequestStatus } from '@skynet/data-ws-core';
import { hasProperty, isString } from '@skynet/type-guards';

/**
 * Websocket commands behind the sub-accounts page's create and edit modals.
 * These are wire-protocol names and must match the backend.
 */
export enum ApiCommands {
  CreateAPIKey = 'CreateAPIKey',
  UpdateAPIKey = 'UpdateAPIKey',
  CreateSubAccount = 'CreateSubAccount',
}

/** `new` creates a key, `edit` renames an existing one, `sub` adds a sub-account. */
export type ModalType = 'new' | 'edit' | 'sub';

/** An existing API key, opened for editing its label. */
export interface InitialNewValuesApiKey {
  AccountID: string;
  ID: number;
  exchange: number;
}

/** Narrows modal initial values to an existing key (as opposed to a create prefill). */
export function isInitialNewValuesApiKey(value: unknown): value is InitialNewValuesApiKey {
  return (
    value !== null &&
    typeof value === 'object' &&
    'AccountID' in value &&
    'ID' in value &&
    'exchange' in value
  );
}

/** UI state of the create/edit API key and add sub-account modals. */
export interface ApiKeysState {
  isOpenModal: boolean;
  modalInitialValue?: OpenModalType['initialValues'];
  typeModal?: ModalType;
  selectedExchange: number | undefined;

  reqApiKey: RequestStatus;
  /** The last failure of a create, edit or add-sub-account request. */
  error: GenericError | null;
}

export type OpenModalType =
  | { type: 'new'; initialValues?: CreateApiKeyPrefill }
  | { type: 'edit'; initialValues: InitialNewValuesApiKey }
  | { type: 'sub'; initialValues: AddSubAccountPrefill };

export interface CreateApiKeyPayload {
  apiKey: string;
  exchange: number;
  label: string;
  name: string;
  secret?: string;
  passphrase?: string;
}

export interface UpdateApiKeyPayload {
  id: number;
  label: string;
}

export interface CreateSubAccountPayload {
  exchange: number;
  name: string;
  label: string;
}

/** Prefill of the add sub-account form: the main account it is added to. */
export interface AddSubAccountPrefill {
  exchange: number;
  /**
   * Labels of the main account's own API keys, at least one. `CreateSubAccount`
   * creates the sub-account on the exchange through one of them, sent as `label`.
   */
  apiKeyLabels: string[];
}

/** Narrows modal initial values to an add sub-account prefill. */
export function isAddSubAccountPrefill(value: unknown): value is AddSubAccountPrefill {
  return (
    hasProperty(value, 'exchange') &&
    typeof value.exchange === 'number' &&
    hasProperty(value, 'apiKeyLabels') &&
    Array.isArray(value.apiKeyLabels) &&
    value.apiKeyLabels.length > 0 &&
    value.apiKeyLabels.every(isString)
  );
}

/** Prefill of the create form: the exchange of the account a key is added to. */
export type CreateApiKeyPrefill = Pick<CreateApiKeyPayload, 'exchange'>;
